import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getAccessToken } from '@/lib/tokenStorage';
import {
  type InactiveSireError,
  translateInactiveSireError,
  translateInactiveSireErrors,
} from '@/utils/sire';

import { semenApi } from './api';
import {
  AddSemenInput,
  AddSemenToShipmentInput,
  AddSemenTransactionInput,
  AddSemenTransactionV2Input,
  AddStorageLocationInput,
  EditSemenInput,
  RemoveSemenFromShipmentInput,
  SemenPayload,
  SemenTransactionQueryDto,
} from './types';

export const createSemenOptions = (
  searchParams: URLSearchParams = new URLSearchParams(),
  useV3: boolean = false,
) => ({
  queryKey: ['semen', 'list', searchParams.toString(), useV3 ? 'v3' : 'v2'],
  queryFn: () => semenApi.getSemen(searchParams, useV3),
});

export const useSemen = (query: { semenId?: string } = {}) => {
  const useV3 = useFeatureFlag('Semen_Data_Points');

  const searchParams = new URLSearchParams();
  if (query.semenId) {
    searchParams.append('id', query.semenId);
  }
  return useQuery({
    ...createSemenOptions(searchParams, useV3),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAddSemen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenInput) => semenApi.addSemen(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export function useAddSemenV2() {
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(input: AddSemenInput): Promise<SemenPayload> {
      const token = getAccessToken();
      if (!token) {
        throw new Error('No access token available. Please log in.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/v2/semen`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(input),
        },
      );

      if (response.status === 400) {
        const body = await response.text();
        const message = translateInactiveSireErrors(body);
        return message ? { success: false, message } : JSON.parse(body);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
}

export const useAddStorageLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddStorageLocationInput) =>
      semenApi.addStorageLocation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storageLocation'] });
    },
  });
};

export const useEditSemen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: EditSemenInput[]) => semenApi.editSemen(inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export function useEditSemenV2() {
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(inputs: EditSemenInput[]): Promise<SemenPayload[]> {
      const token = getAccessToken();
      if (!token) {
        throw new Error('No access token available. Please log in.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/v2/semen`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(inputs),
        },
      );

      if (response.status === 400) {
        const failures: Array<SemenPayload | InactiveSireError> =
          await response.json();
        return failures.map((failure) => ({
          success: false,
          message:
            'code' in failure
              ? translateInactiveSireError(failure)
              : failure.message,
        }));
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`,
        );
      }

      return await response.json();
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
}

export const createSemenTransactionOptions = (
  searchParams: URLSearchParams = new URLSearchParams(),
) => ({
  queryKey: ['semen', 'transactions', searchParams.toString()],
  queryFn: () => semenApi.getSemenTransactionsBySemenIdV2(searchParams),
});

export const useSemenTransactionsV2 = (
  query: SemenTransactionQueryDto = {},
  options?: { enabled?: boolean },
) => {
  const searchParams = new URLSearchParams();
  if (query.semenId) {
    searchParams.append('semenId', query.semenId);
  }
  return useQuery({
    ...createSemenTransactionOptions(searchParams),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useSemenTransactions = (
  query: SemenTransactionQueryDto = {},
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['semen', 'transactions', query],
    queryFn: () => semenApi.getSemenTransactionsBySemenId(query),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useAddSemenTransactionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenTransactionV2Input) =>
      semenApi.addSemenTransactionV2(input),
    onSuccess: (_, variables) => {
      if (variables.transactions) {
        variables.transactions.forEach((transaction) => {
          queryClient.invalidateQueries({
            queryKey: ['semen', 'transactions', transaction.semenId],
          });
        });
      }
      queryClient.invalidateQueries({ queryKey: ['semen', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export const useAddSemenTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenTransactionInput) =>
      semenApi.addSemenTransaction(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['semen', 'transactions', variables.semenId],
      });
      queryClient.invalidateQueries({ queryKey: ['semen', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export const useFertilizationsBySemenId = (semenId: string) => {
  return useQuery({
    queryKey: ['semen', 'fertilizations', semenId],
    queryFn: () => semenApi.getFertilizationsBySemenId(semenId),
    enabled: Boolean(semenId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAddSemenToShipment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenToShipmentInput) =>
      semenApi.addSemenToShipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen', 'shipments'] });
    },
  });
};

export const useRemoveSemenFromShipment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RemoveSemenFromShipmentInput) =>
      semenApi.removeSemenFromShipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen', 'shipments'] });
    },
  });
};
